import React, { useState } from "react";
import BookingCard from "./BookingCard";
import { Booking } from "../types/booking";
import AppointmentSidebar from "./AppointmentSidebar";
import BlockTimeSidebar from "./BlockTimeSidebar";
import { getBookingEdit } from "../services/bookings";
import { getBlockTime, BlockTime } from "../services/blockTimes";
import { getBusinessServices } from "../services/businessServicesApi";
import { Ban, CalendarPlus, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import * as Popover from "@radix-ui/react-popover";
import { useToast } from "../hooks/useToast";
import GlobalLoader from "./ui/GlobalLoader";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { canCreateBooking } from "../lib/permissions";

interface CalendarGridProps {
  bookings: Booking[];
  selectedStaff: string | null;
  view: "day" | "week";
  selecteddate: Date;
  staffList?: { id: number; name: string; color: string }[];
  onRefresh?: () => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  bookings,
  selectedStaff,
  view,
  selecteddate,
  staffList: propStaffList,
  onRefresh,
}) => {
  const toast = useToast();
  const { user } = useAuth();
  const allowCreateBooking = canCreateBooking(user);
  const [selectedSlot, setSelectedSlot] = useState<{
    time: string;
    staff: string;
    date: string;
  } | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [slotChoiceOpen, setSlotChoiceOpen] = useState(false);
  const [isBlockSidebarOpen, setIsBlockSidebarOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<BlockTime | null>(null);

  const [rebookMode, setRebookMode] = useState(false);
  const [filteredServices, setFilteredServices] = useState<any[]>([]);
  const [activeWeekSlot, setActiveWeekSlot] = React.useState<{
    hour: number;
    minute: number;
    dayIdx: number;
  } | null>(null);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  const chunkRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});

  const handleMobileHorizontalScroll = (chunkIndex: number, direction: 'left' | 'right') => {
    const container = chunkRefs.current[chunkIndex];
    if (container) {
      const scrollAmount = 240;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const weekDays = [
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
  ];

  // Helper function to detect if two bookings overlap
  const doBookingsOverlap = (booking1: Booking, booking2: Booking): boolean => {
    const start1 = new Date(booking1.start).getTime();
    const end1 = new Date(booking1.end).getTime();
    const start2 = new Date(booking2.start).getTime();
    const end2 = new Date(booking2.end).getTime();

    return start1 < end2 && start2 < end1;
  };

  // Helper to calculate overlap columns for a specific set of bookings
  const getOverlapCount = (bookingsForCol: Booking[]) => {
    if (bookingsForCol.length === 0) return 1;
    const sorted = [...bookingsForCol].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    const tempCols: Booking[][] = [];
    sorted.forEach(booking => {
      let placed = false;
      for (let i = 0; i < tempCols.length; i++) {
        const col = tempCols[i];
        if (!doBookingsOverlap(col[col.length - 1], booking)) {
          col.push(booking);
          placed = true;
          break;
        }
      }
      if (!placed) tempCols.push([booking]);
    });
    return Math.max(1, tempCols.length);
  };

  const getBookingsForWeekDay = (
    bookings: Booking[],
    staffMember: { id: number; name: string },
    dayIdx: number
  ) => {
    return bookings.filter((b) => {
      const sameStaff =
        (b.employeeId &&
          staffMember.id &&
          Number(b.employeeId) === Number(staffMember.id)) ||
        (b.staff || "").trim().toLowerCase() ===
          (staffMember.name || "").trim().toLowerCase();
      if (!sameStaff) return false;

      const bookingDate = new Date(b.start);

      // CALCULATE WEEK START (MONDAY)
      const bookingDayOfWeek = bookingDate.getDay(); // 0=Sunday
      const mondayOffset = bookingDayOfWeek === 0 ? -6 : 1 - bookingDayOfWeek;
      const weekStart = new Date(bookingDate);
      weekStart.setHours(0, 0, 0, 0);
      weekStart.setDate(bookingDate.getDate() + mondayOffset);

      //DIFFERENCE IN THE BOOKING DATE AND WEEK START
      const diffDays = Math.floor(
        (bookingDate.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24)
      );

      return diffDays === dayIdx;
    });
  };


  const staff = propStaffList || [];
  const displayedStaff = selectedStaff
    ? staff.filter((s) => s.name === selectedStaff)
    : staff;

  const bookingBelongsToStaff = (
    b: Booking,
    staffMember: { id: number; name: string }
  ) => {
    if (b.employeeId && staffMember.id && Number(b.employeeId) === Number(staffMember.id)) {
      return true;
    }
    return (
      (b.staff || "").trim().toLowerCase() ===
      (staffMember.name || "").trim().toLowerCase()
    );
  };

  const itemsToPaginate = view === "day" ? displayedStaff : weekDays;

  const isDesktop = windowWidth >= 1024;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const chunkSize = isTablet ? 2 : 1; // Used only for mobile/tablet looping

  // Helper helper to get column width
  const getColWidth = (index: number) => {
    return Math.max(120, getSlotsNeeded(index) * 120);
  };

  // Consolidate overlap calculation logic
  const getStaffOverlaps = (staffName: string) => {
    const staffBookings = bookings.filter(
      (b) => b.staff?.trim().toLowerCase() === staffName?.trim().toLowerCase()
    );
    return getOverlapCount(staffBookings);
  };

  const getSlotsNeeded = (index: number) => {
    if (index < 0 || index >= itemsToPaginate.length) return 0;
    if (view === "day") {
      const s = (itemsToPaginate as any[])[index];
      return getStaffOverlaps(s.name);
    } else {
      const day = itemsToPaginate[index] as string;
      const dayIdx = weekDays.indexOf(day);
      const dayBookings = displayedStaff.flatMap((staffMember) =>
        getBookingsForWeekDay(bookings, staffMember, dayIdx)
      );
      return getOverlapCount(dayBookings);
    }
  };

  const VISIBLE_COUNT = useMemo(() => {
    if (!isDesktop) return itemsToPaginate.length;

    const maxSlots = view === "day" ? 4 : (windowWidth < 1400 ? 5 : 7);
    let count = 0;
    let slotsUsed = 0;

    for (let i = startIndex; i < itemsToPaginate.length; i++) {
      const needed = getSlotsNeeded(i);
      if (count === 0) {
        slotsUsed += needed;
        count++;
      } else if (slotsUsed + needed <= maxSlots) {
        slotsUsed += needed;
        count++;
      } else {
        break;
      }
    }
    return Math.max(1, count);
  }, [view, windowWidth, startIndex, itemsToPaginate, bookings, displayedStaff]); // Added bookings and displayedStaff to deps

  const handleNextPage = () => {
    setStartIndex(Math.min(itemsToPaginate.length - 1, startIndex + VISIBLE_COUNT));
  };

  const handlePrevPage = () => {
    const maxSlots = view === "day" ? 4 : (windowWidth < 1400 ? 3 : 7);
    let count = 0;
    let slotsUsed = 0;

    for (let i = startIndex - 1; i >= 0; i--) {
      const needed = getSlotsNeeded(i);
      if (count === 0) {
        slotsUsed += needed;
        count++;
      } else if (slotsUsed + needed <= maxSlots) {
        slotsUsed += needed;
        count++;
      } else {
        break;
      }
    }
    const prevCount = Math.max(1, count);
    setStartIndex(Math.max(0, startIndex - prevCount));
  };



  // Use props for staff list, fallback to empty array if undefined
  const staffList = staff;

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);



  const location = useLocation();

  React.useEffect(() => {
    if (location.state?.rebookMode && location.state?.bookingData) {
      setSelectedBooking(location.state.bookingData);
      setRebookMode(true);
      setIsSidebarOpen(true);
      // Optional: Clear state to avoid reopening on refresh
      window.history.replaceState({ ...location.state, rebookMode: false }, "");
    }
  }, [location]);

  // Reset pagination when staff selection, view, or staff count changes
  React.useEffect(() => {
    setStartIndex(0);
  }, [selectedStaff, view, displayedStaff.length]);

  const timeSlots = [
    "9 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "1 PM",
    "2 PM",
    "3 PM",
    "4 PM",
    "5 PM",
    "6 PM",
    "7 PM",
    "8 PM",
    "9 PM",
    "10 PM",
    "11 PM",
    "12 AM",
    "1 AM",
    "2 AM",
    "3 AM",
    "4 AM",
    "5 AM",
    "6 AM",
    "7 AM",
    "8 AM",
  ];

  const rowHeight = 100;

  const MIN_BOOKING_HEIGHT = 40;

  const getBookingPosition = (booking: Booking) => {
    const startDate = new Date(booking.start);
    const endDate = new Date(booking.end);

    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();
    const endHour = endDate.getHours();
    const endMinute = endDate.getMinutes();

    const adjustedStartHour = startHour < 9 ? startHour + 24 : startHour;
    let adjustedEndHour = endHour < 9 ? endHour + 24 : endHour;

    // Handle wrap-around for bookings ending the next logical grid morning
    if (adjustedEndHour < adjustedStartHour) adjustedEndHour += 24;

    const startFromNine = (adjustedStartHour - 9) * 60 + startMinute;
    const endFromNine = (adjustedEndHour - 9) * 60 + endMinute;

    let duration = endFromNine - startFromNine;
    if (duration <= 0) duration = 15;

    const top = (startFromNine * rowHeight) / 60;
    let height = (duration * rowHeight) / 60;

    if (height < MIN_BOOKING_HEIGHT) height = MIN_BOOKING_HEIGHT;

    return { top: `${top}px`, height: `${height}px` };
  };



  // Calculate overlap groups and positions for bookings
  const calculateOverlapLayout = (bookingsForStaff: Booking[]) => {
    if (bookingsForStaff.length === 0) return new Map();

    const sortedBookings = [...bookingsForStaff].sort((a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    const bookingLayouts: Map<number, { column: number; totalColumns: number }> = new Map();
    const columns: Booking[][] = [];

    // Step 1: Greedy column assignment
    sortedBookings.forEach((booking) => {
      let placed = false;
      for (let i = 0; i < columns.length; i++) {
        const column = columns[i];
        const lastInColumn = column[column.length - 1];
        if (!doBookingsOverlap(lastInColumn, booking)) {
          column.push(booking);
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([booking]);
      }
    });

    // Step 2: Assign column index and cluster bookings
    const clusters: Set<Booking>[] = [];
    sortedBookings.forEach((booking) => {
      // Find or create a cluster for this booking
      let targetCluster = clusters.find(cluster =>
        Array.from(cluster).some(b => doBookingsOverlap(b, booking))
      );

      if (!targetCluster) {
        targetCluster = new Set();
        clusters.push(targetCluster);
      }
      targetCluster.add(booking);

      // Merge clusters if this booking bridges them
      const otherOverlappingClusters = clusters.filter(c =>
        c !== targetCluster && Array.from(c).some(b => doBookingsOverlap(b, booking))
      );

      otherOverlappingClusters.forEach(c => {
        c.forEach(b => targetCluster?.add(b));
        clusters.splice(clusters.indexOf(c), 1);
      });
    });

    // Step 3: Calculate max columns per cluster and assign layouts
    clusters.forEach(cluster => {
      const clusterArray = Array.from(cluster);
      let maxColsInCluster = 0;

      clusterArray.forEach(b => {
        for (let i = 0; i < columns.length; i++) {
          if (columns[i].includes(b)) {
            maxColsInCluster = Math.max(maxColsInCluster, i + 1);
            break;
          }
        }
      });

      clusterArray.forEach(b => {
        let colIdx = 0;
        for (let i = 0; i < columns.length; i++) {
          if (columns[i].includes(b)) {
            colIdx = i;
            break;
          }
        }
        bookingLayouts.set(b.id, {
          column: colIdx,
          totalColumns: maxColsInCluster,
        });
      });
    });

    return bookingLayouts;
  };

  const formatTime = (hour: number, minute: number) =>
    new Date(0, 0, 0, hour, minute).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const getDateForWeekDay = (selecteddate: Date, dayIdx: number) => {
    const startOfWeek = new Date(selecteddate);
    const day = startOfWeek.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    startOfWeek.setDate(startOfWeek.getDate() + diff + dayIdx);
    return startOfWeek.toLocaleDateString("en-CA");
  };

  const handleSlotClick = (
    hour: number,
    minute: number,
    staffName: string,
    forcedDate?: string
  ) => {
    const time = formatTime(hour, minute);
    const date = forcedDate || selecteddate.toLocaleDateString("en-CA");

    setSelectedSlot({ time, staff: staffName, date });
    setSelectedBooking(null);
    setRebookMode(false);
    setEditingBlock(null);
    setSlotChoiceOpen(true);
  };

  const openAppointmentFromSlot = () => {
    if (!selectedSlot) return;
    setSlotChoiceOpen(false);
    setIsSidebarOpen(true);
    setFilteredServices([]);
    setServicesLoading(true);

    const stylist = staffList.find(
      (s) =>
        s.name.trim().toLowerCase() ===
        (selectedSlot.staff || "").trim().toLowerCase()
    );
    const stylist_id = stylist?.id || 1;
    void stylist_id;

    getBusinessServices()
      .then((allServices) => {
        const mappedServices = allServices.map(s => ({
          id: s.id,
          name: s.name,
          duration: s.durationLabel,
          price: String(s.price),
          company_id: Number((s.raw as any)?.company_id || 0),
          staffIds: s.staff.map(m => Number(m.id)),
          categoryId: (s.raw as any)?.category_id,
          categoryName: s.categoryName,
          locationId: (s.raw as any)?.location_id,
          description: (s.raw as any)?.description,
          discount: (s.raw as any)?.discount,
          discount_type: (s.raw as any)?.discount_type,
          service_type: (s.raw as any)?.service_type,
          tax_ids: (s.raw as any)?.service_taxes?.map((t: any) => Number(t.tax_id || t.id)) ||
            (s.raw as any)?.tax_services?.map((t: any) => Number(t.tax_id || t.id)) || [],
          service_taxes: (s.raw as any)?.service_taxes,
          tax_services: (s.raw as any)?.tax_services
        }));
        setFilteredServices(mappedServices);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load services for this slot");
      })
      .finally(() => setServicesLoading(false));
  };

  const openBlockFromSlot = () => {
    setSlotChoiceOpen(false);
    setEditingBlock(null);
    setIsBlockSidebarOpen(true);
  };

  const handleBookingClick = async (booking: Booking) => {
    if (booking.isBlocked || booking.status === "blocked") {
      const blockId = booking.blockTimeId;
      if (!blockId) return;
      try {
        const block = await getBlockTime(blockId);
        setEditingBlock(block);
        setIsBlockSidebarOpen(true);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load blocked time");
      }
      return;
    }

    const bookingId = booking.bookingId || booking.id;
    setBookingLoading(true);
    try {
      const bookingData = await getBookingEdit(bookingId);
      console.log("Booking data in the FE", bookingData);

      const bookingDetail = bookingData.booking;

      // Build a lookup of employee id → name from the booking's users array
      const employeeLookup: Record<number, string> = {};
      if (Array.isArray(bookingDetail.users)) {
        bookingDetail.users.forEach((u: any) => {
          if (u?.id && u?.name) employeeLookup[Number(u.id)] = u.name;
        });
      }

      const mappedServices = bookingDetail.items.map((item: any) => {
        // Resolve per-item stylist: prefer item.employee_id, then item.employee?.id
        const empId = item.employee_id || item.employee?.id || null;
        const empName = empId
          ? (employeeLookup[Number(empId)] || item.employee?.name || null)
          : (item.employee?.name || null);

        return {
          id: "service-" + (item.business_service_id || item.business_service?.id || item.id),
          name: item.business_service?.name || item.name || "Service",
          duration: item.business_service
            ? `${item.business_service.time} ${item.business_service.time_type}`
            : "60 min",
          time: item.business_service?.time || 60,
          time_type: item.business_service?.time_type || "minutes",
          price: item.business_service?.price || item.unit_price || "0",
          staffIds: item.business_service?.users?.map((u: any) => Number(u.id)) || [],
          categoryId: item.business_service?.category_id || null,
          locationId: item.business_service?.location_id || null,
          description: item.business_service?.description || "",
          discount: item.business_service?.discount || 0,
          discount_type: item.business_service?.discount_type || "percent",
          service_type: item.business_service?.service_type || "offline",
          tax_ids: (Array.isArray(item.tax_ids) ? item.tax_ids :
            (typeof item.tax_ids === 'string' ? item.tax_ids.split(',').map(Number) :
              (item.business_service?.service_taxes?.map((t: any) => Number(t.tax_id || t.id)) ||
                item.business_service?.tax_services?.map((t: any) => Number(t.tax_id || t.id)) ||
                item.business_service?.taxes?.map((t: any) => Number(t.id)) || []))),
          service_taxes: item.service_taxes || item.business_service?.service_taxes || item.business_service?.taxes,
          tax_services: item.tax_services || item.business_service?.tax_services,
          categoryColor: item.business_service?.category?.color_code || null,
          // Per-service stylist stamped at booking time
          assignedStylistId: empId ? Number(empId) : undefined,
          assignedStylistName: empName || undefined,
        };
      });

      const categoryColor = mappedServices[0]?.categoryColor || null;

      const existingBooking = bookings.find((item) => item.id === bookingId);

      const amountToPay = Number(
        bookingDetail.amount_to_pay ??
          (bookingData as any)?.amount_to_pay ??
          existingBooking?.amountToPay ??
          0,
      );
      const amountPaid = Number(
        bookingDetail.amount_paid ??
          (bookingData as any)?.total_paid ??
          (bookingData as any)?.amount_paid ??
          existingBooking?.amountPaid ??
          0,
      );
      const pendingAmount = Number(
        bookingDetail.pending_amount ??
          (bookingData as any)?.total_pending ??
          (bookingData as any)?.pending_amount ??
          existingBooking?.pendingAmount ??
          Math.max(0, amountToPay - amountPaid),
      );

      const mappedBooking = {
        id: bookingId,
        customer: bookingDetail.user ? {
          id: bookingDetail.user.id,
          name: bookingDetail.user.name,
          email: bookingDetail.user.email,
          phone: bookingDetail.user.phone || bookingDetail.user.formatted_mobile,
          user_image_url: bookingDetail.user.user_image_url
        } : { id: 0, name: "Unknown Client", email: "" },
        services: mappedServices,
        service: mappedServices[0] || null,
        note: bookingDetail.additional_notes || "",
        status: (() => {
          const s = (bookingDetail.status || "").toLowerCase();
          if (s === "in progress" || s === "in_progress") return "in_progress";
          if (s === "no show" || s === "no_show") return "no_show";
          return s;
        })(),
        date: bookingDetail.date || bookingDetail.booking_date || bookingDetail.start?.split(" ")[0] || bookingDetail.date_time?.split(" ")[0] || new Date().toISOString().split("T")[0],
        type: bookingDetail.booking_type,
        categoryColor: categoryColor,
        staffName: bookingDetail.users?.[0]?.name || "",
        tax_amount: bookingDetail.tax_amount || bookingDetail.total_tax,
        tax_name: bookingDetail.tax_name,
        tax_percent: bookingDetail.tax_percent,
        amount_to_pay: amountToPay,
        amount_paid: amountPaid,
        pending_amount: pendingAmount,
        payment_status: bookingDetail.payment_status,
        rawBooking: bookingDetail,
      };

      console.log("---------------------", mappedBooking);

      // Prioritize existingBooking.start since it's already correctly mapped in the parent
      const startStr = existingBooking?.start || bookingData.booking.date_time || bookingData.booking.start;

      let validStartDate: Date;
      if (startStr) {
        // Handle potential formats (ISO, space-separated, etc.)
        const isoStr = startStr.includes(" ") ? startStr.replace(" ", "T") : startStr;
        const parsedDate = new Date(isoStr);
        validStartDate = isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
      } else {
        validStartDate = new Date();
      }

      const dateStr = startStr ? startStr.split(/[ T]/)[0] : new Date().toLocaleDateString("en-CA");

      setSelectedSlot({
        time: validStartDate.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        staff: bookingData.booking.staffName || existingBooking?.staff || "",
        date: dateStr,
      });

      setSelectedBooking(mappedBooking);
      setIsSidebarOpen(true);
      setRebookMode(false);
    } catch (err) {
      console.error("Error fetching booking edit:", err);
      toast.error("Failed to retrieve booking details");
    } finally {
      setBookingLoading(false);
    }
  };







  const visibleStaff = isDesktop && view === "day"
    ? displayedStaff.slice(startIndex, startIndex + VISIBLE_COUNT)
    : displayedStaff;

  const visibleDays = isDesktop && view === "week"
    ? weekDays.slice(startIndex, startIndex + VISIBLE_COUNT)
    : weekDays;


  const canNext = isDesktop && startIndex + VISIBLE_COUNT < itemsToPaginate.length;
  const canPrev = isDesktop && startIndex > 0;

  const actualColCount = isDesktop
    ? (Math.min(itemsToPaginate.length, VISIBLE_COUNT))
    : 7;

  const gridColumns = useMemo(() => {
    const timeCol = "80px";
    let colWidths: string[] = [];

    if (view === "day") {
      colWidths = visibleStaff.map((s, idx) => {
        const staffIdx = startIndex + idx;
        const overlaps = getSlotsNeeded(staffIdx);
        // Base min-width is overlaps * 100px to keep columns legible
        // Using overlaps as fr to make it proportional
        return `minmax(${overlaps * 120}px, ${overlaps}fr)`;
      });
    } else {
      colWidths = visibleDays.map((day, idx) => {
        const dayIndex = startIndex + idx;
        const overlaps = getSlotsNeeded(dayIndex);
        return `minmax(${overlaps * 120}px, ${overlaps}fr)`;
      });
    }

    if (colWidths.length === 0) return `${timeCol} 1fr`;
    return `${timeCol} ${colWidths.join(" ")}`;
  }, [view, visibleStaff, visibleDays, bookings, displayedStaff, startIndex]);

  // Helper to chunk array
  const chunkArray = <T,>(arr: T[], size: number): T[][] => {
    const res: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      res.push(arr.slice(i, i + size));
    }
    return res;
  };

  const staffChunks = view === 'day' ? chunkArray(displayedStaff, chunkSize) : [];
  const dayChunks = view === 'week' ? chunkArray(weekDays, chunkSize) : [];
  const chunks = view === 'day' ? staffChunks : dayChunks;

  const [expandedChunkIndices, setExpandedChunkIndices] = useState<number[]>([]);

  const toggleChunkHelper = (index: number) => {
    setExpandedChunkIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  return (
    <div className="flex flex-col w-full relative pb-10 md:mt-4 lg:mt-0">

      {/* DESKTOP VIEW MOCKUP (Restored Pagination) */}
      {isDesktop && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto max-w-full custom-scrollbar">
          <div className="flex flex-col min-w-max">
            {/* Header Row */}
            <div className="grid flex-1" style={{ gridTemplateColumns: gridColumns }}>
              <div className="sticky left-0 z-40 h-12 border-b border-r border-gray-200 bg-white flex items-center justify-center gap-1">
                {itemsToPaginate.length > VISIBLE_COUNT && (
                  <>
                    <button
                      onClick={handlePrevPage}
                      disabled={!canPrev}
                      className="p-1 hover:bg-gray-50 disabled:opacity-20 disabled:cursor-not-allowed text-[#885ABB] rounded-md transition-colors"
                      title="Scroll Left"
                    >
                      <ChevronLeft size={20} strokeWidth={3} />
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={!canNext}
                      className="p-1 hover:bg-gray-50 disabled:opacity-20 disabled:cursor-not-allowed text-[#885ABB] rounded-md transition-colors"
                      title="Scroll Right"
                    >
                      <ChevronRight size={20} strokeWidth={3} />
                    </button>
                  </>
                )}
              </div>
              {view === "day" ? (
                visibleStaff.length > 0 ? (
                  visibleStaff.map((s, index) => (
                    <div
                      key={`${s.name}-${index}`}
                      style={{ backgroundColor: s.color }}
                      className="text-white text-center py-3 font-medium border-r border-gray-200 last:border-r-0 truncate px-2"
                    >
                      {s.name}
                    </div>
                  ))
                ) : (
                  <div className="bg-gray-50 border-b border-gray-200 flex items-center justify-center text-gray-500 font-medium">
                    No Staff found
                  </div>
                )
              ) : (
                visibleDays.map((day) => (
                  <div
                    key={day}
                    style={{ backgroundColor: "#885ABB" }}
                    className="text-white text-center py-3 font-medium border-r border-gray-200 last:border-r-0"
                  >
                    {day}
                  </div>
                ))
              )}
            </div>

            {/* Body Row */}
            <div className="grid flex-1" style={{ gridTemplateColumns: gridColumns }}>

              {/* Time Column */}
              <div className="sticky left-0 z-30 border-r border-gray-200 bg-white">
                <div className="border-b border-gray-200"></div>
                {timeSlots.map((time) => (
                  <div key={time} className="h-[100px] border-b border-gray-200 flex items-start justify-end pr-3 pt-1">
                    <span className="text-sm text-gray-500">{time}</span>
                  </div>
                ))}
              </div>

              {/* Staff / Days Columns */}
              {view === "day" && visibleStaff.map((staffMember, index) => (
                <div key={staffMember.name} className="relative border-r border-l border-gray-200 last:border-r-0 overflow-visible">
                  <div className="border-b border-gray-200"></div>
                  {timeSlots.map((_, hourIdx) => {
                    const baseHour = 9 + hourIdx;
                    return (
                      <React.Fragment key={hourIdx}>
                        {[0, 15, 30, 45].map((m) => (
                          <div
                            key={m}
                            className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}
                            onClick={() => handleSlotClick(baseHour, m, staffMember.name)}
                          >
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  {/* Bookings */}
                  {(() => {
                    const staffBookings = bookings.filter((b) =>
                      bookingBelongsToStaff(b, staffMember)
                    );
                    const overlapLayout = calculateOverlapLayout(staffBookings);

                    return staffBookings.flatMap((booking) => {
                      const startH = new Date(booking.start).getHours();
                      const endH = new Date(booking.end).getHours();
                      const adjStart = startH < 9 ? startH + 24 : startH;
                      const adjEnd = endH < 9 ? endH + 24 : endH;

                      // Check if booking wraps from bottom (late) to top (early)
                      if (adjStart > adjEnd) {
                        try {
                          const originalStart = new Date(booking.start);
                          const splitDate = new Date(originalStart);
                          splitDate.setHours(9, 0, 0, 0);

                          const toLocalIso = (d: Date) => {
                            const pad = (n: number) => n < 10 ? '0' + n : n;
                            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
                          };

                          // Bottom Part: Original Start -> 9:00 AM (Next logical day effectively)
                          const bottomPart = {
                            ...booking,
                            originalId: booking.id,
                            id: Number(`${booking.id}991`), // Temporary ID
                            start: booking.start,
                            end: toLocalIso(splitDate),
                            originalStart: booking.start,
                            originalEnd: booking.end,
                            isSplitBottom: true
                          };

                          // Top Part: 9:00 AM -> Original End
                          const topPart = {
                            ...booking,
                            originalId: booking.id,
                            id: Number(`${booking.id}992`), // Temporary ID
                            start: toLocalIso(splitDate),
                            end: booking.end,
                            originalStart: booking.start, // Pass original times for display
                            originalEnd: booking.end,
                            isSplitTop: true
                          };

                          return [bottomPart, topPart];
                        } catch (e) {
                          return [booking];
                        }
                      }
                      return [booking];
                    }).map((b: any) => {
                      const pos = getBookingPosition(b);
                      // Use originalId for layout lookup if it's a split part
                      const layoutId = b.originalId || b.id;
                      const layout = overlapLayout.get(layoutId) || { column: 0, totalColumns: 1 };
                      const widthPercent = 100 / layout.totalColumns;
                      const leftPercent = layout.column * widthPercent;

                      return (
                        <BookingCard
                          key={`${b.id}-card`}
                          booking={{
                            ...b,
                            start: b.originalStart || b.start,
                            end: b.originalEnd || b.end,
                          }}
                          bgColor={staffMember.color}
                          style={{
                            top: pos.top,
                            height: pos.height,
                            position: "absolute",
                            width: `calc(${widthPercent}% - 1px)`,
                            left: `${leftPercent}%`,
                            zIndex: 20,
                          }}
                          onClick={() => handleBookingClick(b)}
                          isLastColumn={
                            index === visibleStaff.length - 1 ||
                            (visibleStaff.length >= 4 && index === visibleStaff.length - 2)
                          }
                          showAbove={visibleStaff.length === 1}
                        />
                      );
                    });
                  })()}
                </div>
              ))}

              {view === "week" && visibleDays.map((day) => {
                const dayIdx = weekDays.indexOf(day);
                return (
                  <div key={day} className="relative border-r border-l border-gray-200 last:border-r-0 overflow-visible">
                    <div className="border-b border-gray-200"></div>
                    {timeSlots.map((_, hourIdx) => {
                      const baseHour = 9 + hourIdx;
                      return (
                        <React.Fragment key={hourIdx}>
                          {[0, 15, 30, 45].map((m) => (
                            <div
                              key={m}
                              className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}
                            >
                              <Popover.Root
                                open={activeWeekSlot?.hour === baseHour && activeWeekSlot?.minute === m && activeWeekSlot?.dayIdx === dayIdx}
                                onOpenChange={(open) => !open && setActiveWeekSlot(null)}
                              >
                                <Popover.Trigger className="absolute inset-0 w-full h-full" onClick={() => setActiveWeekSlot({ hour: baseHour, minute: m, dayIdx })} />
                                <Popover.Content side="right" align="start" className="w-48 bg-white rounded-lg shadow-lg border border-gray-300 z-50">
                                  <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-700">Select Staff Member</div>
                                  <div className="flex flex-col p-2 gap-1 max-h-48 overflow-y-auto">
                                    {displayedStaff.map(s => (
                                      <button key={s.name || s.id} className="flex items-center px-3 py-2 rounded hover:bg-purple-50 text-sm text-gray-800 font-medium transition"
                                        onClick={() => {
                                          const computedDate = getDateForWeekDay(selecteddate, dayIdx);
                                          handleSlotClick(baseHour, m, s.name, computedDate);
                                          setActiveWeekSlot(null);
                                        }}
                                      >
                                        <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: s.color }} />
                                        {s.name}
                                      </button>
                                    ))}
                                  </div>
                                  <Popover.Arrow className="fill-white" />
                                </Popover.Content>
                              </Popover.Root>
                            </div>
                          ))}
                        </React.Fragment>
                      );
                    })}

                    {/* Week View Bookings */}
                    {(() => {
                      // For the week view, we need to collect ALL bookings for ALL staff on this day
                      // to calculate the correct overlap layout for the entire day column.
                      const allBookingsForDay = displayedStaff.flatMap(staffMember =>
                        getBookingsForWeekDay(bookings, staffMember, dayIdx)
                      );
                      const overlapLayout = calculateOverlapLayout(allBookingsForDay);

                      return allBookingsForDay.map(b => {
                        const pos = getBookingPosition(b);
                        const layout = overlapLayout.get(b.id) || { column: 0, totalColumns: 1 };
                        const widthPercent = 100 / layout.totalColumns;
                        const leftPercent = layout.column * widthPercent;
                        const staffMember = displayedStaff.find(
                          (s) =>
                            (b.employeeId && Number(b.employeeId) === Number(s.id)) ||
                            (s.name || "").trim().toLowerCase() ===
                              (b.staff || "").trim().toLowerCase()
                        );

                        return (
                          <BookingCard
                            key={b.id}
                            booking={b}
                            bgColor={staffMember?.color || "#885ABB"}
                            style={{
                              top: pos.top,
                              height: pos.height,
                              position: "absolute",
                              width: `${widthPercent}%`,
                              left: `${leftPercent}%`
                            }}
                            onClick={() => handleBookingClick(b)}
                            isLastColumn={dayIdx >= 5}
                          />
                        );
                      });
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* MOBILE / TABLET VIEW (Responsive Chunking) */}
      {!isDesktop && (
        <div className="flex flex-col gap-8 w-full">
          {(!displayedStaff || displayedStaff.length === 0) && view === 'day' && (
            <div className="bg-white p-8 text-center border border-gray-200 rounded-lg">
              <p className="text-gray-500">No staff members assigned</p>
            </div>
          )}
          {chunks.map((chunkItems: any[], chunkIndex) => {
            const chunkGridTemplate = `80px ${chunkItems.map((item, id) => {
              let overlaps = 1;
              if (view === "day") {
                const itemIdx = displayedStaff.findIndex(s => s.name === item.name);
                overlaps = itemIdx >= 0 ? getSlotsNeeded(itemIdx) : 1;
              } else {
                const dayIdx = weekDays.indexOf(item);
                overlaps = dayIdx >= 0 ? getSlotsNeeded(dayIdx) : 1;
              }
              return `minmax(${overlaps * 120}px, ${overlaps}fr)`;
            }).join(" ")}`;
            const isChunkExpanded = expandedChunkIndices.includes(chunkIndex); // Removed isDesktop check here
            const collapsedHeightClass = isTablet ? 'max-h-[350px]' : 'max-h-[150px]';

            return (
              <div key={chunkIndex} className="bg-white border border-gray-200 rounded-xl overflow-hidden w-full relative shadow-none lg:shadow-sm">

                {/* SCROLLABLE / CLIPPED CONTENT AREA */}
                <div
                  className={`w-full transition-all duration-500 ease-in-out ${isChunkExpanded ? '' : `${collapsedHeightClass} overflow-hidden`}`}
                >
                  <div
                    ref={(el) => {
                      chunkRefs.current[chunkIndex] = el;
                    }}
                    className="w-full overflow-x-auto custom-scrollbar relative"
                  >
                    {/* HEADER OF CHUNK */}
                    <div className="grid min-w-max" style={{ gridTemplateColumns: chunkGridTemplate }}>
                      <div className="sticky left-0 h-12 bg-white z-[55] border-r border-gray-200 border-b shadow-[2px_0_5px_rgba(0,0,0,0.02)] flex items-center justify-center gap-1.5 px-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMobileHorizontalScroll(chunkIndex, 'left');
                          }}
                          className="p-1.5 hover:bg-gray-100 text-[#885ABB] rounded-md transition-colors active:scale-95 bg-gray-50/50 border border-gray-100"
                          aria-label="Scroll Left"
                        >
                          <ChevronLeft size={18} strokeWidth={3} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMobileHorizontalScroll(chunkIndex, 'right');
                          }}
                          className="p-1.5 hover:bg-gray-100 text-[#885ABB] rounded-md transition-colors active:scale-95 bg-gray-50/50 border border-gray-100"
                          aria-label="Scroll Right"
                        >
                          <ChevronRight size={18} strokeWidth={3} />
                        </button>
                      </div>
                      {view === 'day'
                        ? (chunkItems as typeof displayedStaff).map((s) => (
                          <div key={s.name || s.id} style={{ backgroundColor: s.color }} className="text-white text-center py-3 font-medium border-r border-b border-gray-200 last:border-r-0">
                            {s.name}
                          </div>
                        ))
                        : (chunkItems as string[]).map(day => (
                          <div key={day} style={{ backgroundColor: "#885ABB" }} className="text-white text-center py-3 font-medium border-r border-b border-gray-200 last:border-r-0">
                            {day}
                          </div>
                        ))
                      }
                    </div>

                    {/* BODY OF CHUNK */}
                    <div className="grid min-w-max" style={{ gridTemplateColumns: chunkGridTemplate }}>
                      {/* TIME COLUMN */}
                      <div className="sticky left-0 bg-white z-20 border-r border-gray-200 shadow-[2px_0_5px_rgba(0,0,0,0.05)]">
                        {timeSlots.map((time) => (
                          <div key={time} className="h-[100px] border-b border-gray-200 flex items-start justify-end pr-3 pt-1">
                            <span className="text-sm text-gray-500">{time}</span>
                          </div>
                        ))}
                      </div>

                      {/* COLUMNS */}
                      {view === 'day'
                        ? (chunkItems as typeof displayedStaff).map((staffMember, indexInChunk) => (
                          <div key={staffMember.name || staffMember.id} className="relative border-r border-l border-gray-200 last:border-r-0">
                            {/* Time slots interactions */}
                            {timeSlots.map((_, hourIdx) => {
                              const baseHour = 9 + hourIdx;
                              return (
                                <React.Fragment key={hourIdx}>
                                  {[0, 15, 30, 45].map((m) => (
                                    <div key={m} className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}
                                      onClick={() => handleSlotClick(baseHour, m, staffMember.name)}
                                    >
                                    </div>
                                  ))}
                                </React.Fragment>
                              );
                            })}

                            {/* Bookings */}
                            {(() => {
                              const staffBookings = bookings.filter((b) =>
                                bookingBelongsToStaff(b, staffMember)
                              );
                              const overlapLayout = calculateOverlapLayout(staffBookings);

                              return staffBookings.map((b) => {
                                const pos = getBookingPosition(b);
                                const layout = overlapLayout.get(b.id) || { column: 0, totalColumns: 1 };
                                const widthPercent = 100 / layout.totalColumns;
                                const leftPercent = layout.column * widthPercent;

                                return (
                                  <BookingCard
                                    key={b.id}
                                    booking={b}
                                    bgColor={staffMember.color}
                                    style={{
                                      top: pos.top,
                                      height: pos.height,
                                      position: "absolute",
                                      width: `${widthPercent}%`,
                                      left: `${leftPercent}%`
                                    }}
                                    onClick={() => handleBookingClick(b)}
                                    isLastColumn={indexInChunk === chunkItems.length - 1}
                                    showAbove={chunkItems.length === 1}
                                  />
                                );
                              });
                            })()}
                          </div>
                        ))
                        : (chunkItems as string[]).map((day, indexInChunk) => {
                          const originalDayIdx = weekDays.indexOf(day);
                          return (
                            <div key={day} className="relative border-r border-l border-gray-200 last:border-r-0">
                              {timeSlots.map((_, hourIdx) => {
                                const baseHour = 9 + hourIdx;
                                return (
                                  <React.Fragment key={hourIdx}>
                                    {[0, 15, 30, 45].map((m) => (
                                      <div key={m} className={`relative group hover:bg-purple-50 transition cursor-pointer flex items-center justify-center h-[25px] ${m === 0 || m === 30 ? "border-t border-gray-200" : ""}`}>
                                        <Popover.Root
                                          open={activeWeekSlot?.hour === baseHour && activeWeekSlot?.minute === m && activeWeekSlot?.dayIdx === originalDayIdx}
                                          onOpenChange={(open) => !open && setActiveWeekSlot(null)}
                                        >
                                          <Popover.Trigger className="absolute inset-0 w-full h-full" onClick={() => setActiveWeekSlot({ hour: baseHour, minute: m, dayIdx: originalDayIdx })} />
                                          <Popover.Content side="right" align="start" className="w-48 bg-white rounded-lg shadow-lg border border-gray-300 z-50">
                                            <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-700">Select Staff Member</div>
                                            <div className="flex flex-col p-2 gap-1 max-h-48 overflow-y-auto">
                                              {displayedStaff.map((s) => (
                                                <button key={s.name || s.id} className="flex items-center px-3 py-2 rounded hover:bg-purple-50 text-sm text-gray-800 font-medium transition"
                                                  onClick={() => {
                                                    const computedDate = getDateForWeekDay(selecteddate, originalDayIdx);
                                                    handleSlotClick(baseHour, m, s.name, computedDate);
                                                    setActiveWeekSlot(null);
                                                  }}
                                                >
                                                  <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: s.color }} />
                                                  {s.name}
                                                </button>
                                              ))}
                                            </div>
                                            <Popover.Arrow className="fill-white" />
                                          </Popover.Content>
                                        </Popover.Root>
                                      </div>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                              {(() => {
                                const dayBookings = displayedStaff.flatMap(staffMember =>
                                  getBookingsForWeekDay(bookings, staffMember, originalDayIdx)
                                );
                                const overlapLayout = calculateOverlapLayout(dayBookings);

                                return dayBookings.map((b) => {
                                  const pos = getBookingPosition(b);
                                  const layout = overlapLayout.get(b.id) || { column: 0, totalColumns: 1 };
                                  const widthPercent = 100 / layout.totalColumns;
                                  const leftPercent = layout.column * widthPercent;
                                  const staffMember = displayedStaff.find(
                          (s) =>
                            (b.employeeId && Number(b.employeeId) === Number(s.id)) ||
                            (s.name || "").trim().toLowerCase() ===
                              (b.staff || "").trim().toLowerCase()
                        );

                                  return (
                                    <BookingCard
                                      key={b.id}
                                      booking={b}
                                      bgColor={staffMember?.color || "#885ABB"}
                                      style={{
                                        top: pos.top,
                                        height: pos.height,
                                        position: "absolute",
                                        width: `${widthPercent}%`,
                                        left: `${leftPercent}%`
                                      }}
                                      onClick={() => handleBookingClick(b)}
                                      isLastColumn={indexInChunk === chunkItems.length - 1}
                                      showAbove={chunkItems.length === 1}
                                    />
                                  );
                                });
                              })()}
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                </div>

                {/* FADE OVERLAY & TOGGLE BUTTON (Mobile/Tablet Only) */}
                {!isChunkExpanded && (
                  <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white via-white/95 to-transparent z-30 pointer-events-none" />
                )}

                <button
                  onClick={() => toggleChunkHelper(chunkIndex)}
                  className={`absolute bottom-0 left-0 w-full flex flex-col items-center justify-center py-2 bg-white/95 backdrop-blur-sm border-t border-gray-100 hover:bg-gray-50 transition-all text-[#885ABB] z-40 ${isChunkExpanded ? 'relative border-t-2 shadow-[0_-4px_10px_rgba(0,0,0,0.03)]' : ''}`}
                >
                  <span className="text-[9px] font-black uppercase tracking-widest mb-1 opacity-60">
                    {isChunkExpanded ? "Show Less" : "Expand Full Day"}
                  </span>
                  {isChunkExpanded ? (
                    <ChevronUp className="w-5 h-5" strokeWidth={3} />
                  ) : (
                    <ChevronDown className="w-5 h-5" strokeWidth={3} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {rebookMode && (
        <button className="w-full py-4 bg-[#885ABB] text-white font-extrabold rounded-lg mt-2 mx-auto max-w-[98%] mb-2">
          SELECT A TIME TO REBOOK
        </button>
      )}
      <AppointmentSidebar
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedBooking(null);
          setRebookMode(false);
        }}
        stylist={selectedSlot?.staff || ""}
        appointmentTime={selectedSlot?.time || ""}
        services={filteredServices}
        bookingData={selectedBooking}
        onRebook={() => setRebookMode(true)}
        staffList={staffList}
        setFilteredServices={setFilteredServices}
        servicesLoading={servicesLoading}
        appointmentDate={selectedSlot?.date || ""}
        mode="calendar"
        onRefresh={onRefresh}
        rebookMode={rebookMode}
      />
      <BlockTimeSidebar
        isOpen={isBlockSidebarOpen}
        onClose={() => {
          setIsBlockSidebarOpen(false);
          setEditingBlock(null);
        }}
        onSaved={() => onRefresh?.()}
        staffList={staffList}
        defaultStaffName={selectedSlot?.staff || ""}
        defaultDate={selectedSlot?.date || ""}
        defaultStartTime={selectedSlot?.time || "09:00"}
        editing={editingBlock}
      />
      {slotChoiceOpen && selectedSlot ? (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setSlotChoiceOpen(false)}
          />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-[#402B69]">
              {selectedSlot.staff}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedSlot.date} · {selectedSlot.time}
            </p>
            <div className="mt-5 grid gap-3">
              {allowCreateBooking ? (
                <button
                  type="button"
                  onClick={openAppointmentFromSlot}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left hover:border-[#885ABB] hover:bg-[#885ABB]/5"
                >
                  <span className="rounded-lg bg-[#885ABB]/10 p-2 text-[#885ABB]">
                    <CalendarPlus size={18} />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      New appointment
                    </span>
                    <span className="block text-xs text-gray-500">
                      Book a customer for this slot
                    </span>
                  </span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={openBlockFromSlot}
                className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left hover:border-gray-400 hover:bg-gray-50"
              >
                <span className="rounded-lg bg-gray-100 p-2 text-gray-700">
                  <Ban size={18} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-gray-900">
                    Block time
                  </span>
                  <span className="block text-xs text-gray-500">
                    Mark staff unavailable (break, meeting…)
                  </span>
                </span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setSlotChoiceOpen(false)}
              className="mt-4 w-full rounded-lg py-2 text-sm font-semibold text-gray-500 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {bookingLoading && (
        <GlobalLoader
          initialPageLoad={true}
          description="Loading booking details..."
          compensateSidebar={false}
        />
      )}
    </div>
  );
};

export default CalendarGrid;
